/**
 * landingIframeSrc — pure URL builder for the embedded viewer iframe.
 *
 * Lives in its own module (instead of inline in `LandingPage.tsx`) so
 * unit tests can import the function without dragging in the React
 * tree, SVG assets, and the rest of the landing-page surface area —
 * none of which are relevant to the URL-passthrough contract.
 *
 * See `buildViewerIframeSrc` JSDoc for the exact behavior pinned here.
 */

import { withBase } from '../../../lib/withBase';

/**
 * Param keys the landing page must NOT forward to the embedded
 * viewer.
 *
 * - `playbackURL` is on the denylist because it makes the viewer
 *   fetch arbitrary JSON. If we forwarded it unchecked, a crafted
 *   landing URL (`/?playbackURL=https://attacker.example/x.json`)
 *   would cause the same-origin viewer iframe to fetch attacker
 *   content. The viewer still accepts `playbackURL` for explicit
 *   `/playground.html?playbackURL=…` deep-links — we just don't let
 *   the landing page launder one in.
 *
 * Add entries here for future landing-only keys (analytics like
 * `utm_*`, A/B variant flags) or for any viewer param that should
 * not be attacker-controllable via a landing URL.
 */
export const LANDING_ONLY_PARAMS: ReadonlySet<string> = new Set<string>([
  'playbackURL',
]);

/**
 * Build the iframe `src` for the embedded viewer.
 *
 *   1. Forward EVERY landing-page query param into the iframe URL,
 *      preserving repeated keys, empty values, and URL-encoded
 *      characters. This is what makes deep links + debug flags
 *      "just work" no matter which entry-point the user came in on.
 *   2. Skip params on {@link LANDING_ONLY_PARAMS}.
 *   3. Always end with `embed=1`, overriding any inbound `embed=…`
 *      so the landing URL can never accidentally turn off embed
 *      mode (which would break the iframe's chrome).
 *
 * Returns a same-origin path prefixed with the Vite deploy base
 * (e.g. `/playground.html?…` or `/uma/playground.html?…`). Callers
 * should NOT pass through host/origin information from the landing
 * URL — doing so would let crafted landing URLs redirect the iframe.
 */
export function buildViewerIframeSrc(searchParams: URLSearchParams): string {
  const out = new URLSearchParams();

  // Pass through everything except denylisted keys + any inbound
  // `embed` (we re-set it last). Using `forEach` instead of `for..of`
  // because URLSearchParams iteration shape varies across runtimes
  // (Node vs jsdom vs real browsers); forEach is the lowest common
  // denominator and preserves insertion order.
  searchParams.forEach((value, key) => {
    if (LANDING_ONLY_PARAMS.has(key)) return;
    if (key === 'embed') return; // re-added below
    out.append(key, value);
  });

  // Force `embed=1` last. `set` (not `append`) collapses any
  // duplicate `embed` keys we may have appended above.
  out.set('embed', '1');

  return `${withBase('/playground.html')}?${out.toString()}`;
}
